<template>
    <div>
        <Paper :header="label">
            <div slot="header" class="d-flex flex-row align-items-center">
                <!--
                <h6 v-if="estimatedJobsLeft" class="subtitle bold heading mb-0">
                    {{ estimatedCountText }}
                </h6>
                -->
                <layout-toggles v-if="!noToggles" v-model="isList" />
            </div>

            <div
                :style="{
                    'max-height': restrictHeight ? restrictHeight + 'px' : 'none',
                }"
                :class="{ 'overflow-height': restrictHeight }"
            >
                <div v-if="data.length > 0">
                    <transition-group
                        v-if="isList && showItems"
                        tag="div"
                        name="appear"
                        :style="{ '--total': data.length }"
                    >
                        <PostItem
                            v-for="(item, i) in data"
                            :id="item.id.toString()"
                            :key="i.toString() + '_' + item.id.toString()"
                            :requestId="item.requestId"
                            :eventId="item.eventId"
                            :positionUrl="getPositionUrl(item)"
                            :image="item.companyLogo || defaultLogo()"
                            :title="item.title"
                            :position="item.position"
                            :country="item.country"
                            :location="item.shortLocation"
                            :remote="item.postingRegion === 'TELECOMMUTE'"
                            :company-name="item.companyName"
                            :labels="item.labels"
                            :deadline="item.deadline"
                            :promote="item.promote"
                            :style="{
                                '--i': (i > pageOffset ? i % 25 : i) + 1,
                            }"
                        />
                    </transition-group>

                    <div v-else class="grid-view" :class="{ 'smallView-grid-view': smallView }">
                        <PostingCard
                            v-for="(item, i) in data"
                            :id="item.id.toString()"
                            :key="i.toString() + '_' + item.id.toString()"
                            :requestId="item.requestId"
                            :positionUrl="getPositionUrl(item)"
                            :eventId="item.eventId"
                            :image="item.companyLogo || defaultLogo()"
                            :title="item.title"
                            :position="item.position"
                            :location="item.location"
                            :country="item.country"
                            :company-name="item.companyName"
                            :deadline="item.deadline"
                            :labels="item.labels"
                            :promote="item.promote"
                        />
                    </div>
                </div>

                <div v-if="isLoading || fakeLoad" class="">
                    <ListItemSkeleton v-for="i in 4" :key="i" />
                </div>
                <div v-else-if="data.length === 0">
                    <div
                        v-if="!hasEmptyListSlot()"
                        class="p-4 d-flex flex-column align-items-center"
                    >
                        <h6 class="title bold m-1">
                            {{ $t('shared.postingModule.postingModule.noMatchFound') }}
                        </h6>
                    </div>
                    <slot v-else name="empty" />
                </div>
                <slot />
            </div>
        </Paper>
    </div>
</template>

<script>
import PostItem from './PostItem';
import PostingCard from './PostingCard';
import LayoutToggles from '~/shared/LayoutToggles';
import ListItemSkeleton from '~/components/skeletons/ListItemSkeleton';

// Assets
import DefaultLogo from '~/assets/img/DefaultLogo.png';

// Utils
import i18n from '~/localization/i18n';

export default {
    name: 'PostingModule',
    components: {
        PostItem,
        PostingCard,
        LayoutToggles,
        ListItemSkeleton,
    },
    props: {
        noToggles: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: i18n.t('shared.postingModule.postingModule.positions'),
        },
        data: {
            type: Array,
            default: () => [],
        },
        pageOffset: {
            type: Number,
            default: 0,
        },
        estimatedJobsLeft: {
            type: Number,
            default: 0,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        fakeLoad: {
            type: Boolean,
            default: false,
        },
        smallView: {
            type: Boolean,
            default: false,
        },
        restrictHeight: {
            type: Number,
            default: 0,
        },
        resolveRoute: {
            type: Function,
            default: null,
        },
        defaultView: {
            type: String, // 'list' or 'grid'
            default: 'list',
        },
    },

    data() {
        return {
            isList: !this.smallView && this.defaultView === 'list',
            showItems: false,
            isLoadingMirror: this.isLoading,
        };
    },
    computed: {
        jobs() {
            return [];
        },
        searchResults() {
            return [];
        },

        estimatedCountText() {
            const count = (this.data || []).length;
            const totalCount = this.estimatedJobsLeft;
            return totalCount > 10
                ? this.$t('shared.postingModule.postingModule.estimatedCount', {
                      count,
                      totalCount,
                  })
                : this.$t('shared.postingModule.postingModule.knownCount', {
                      count,
                      totalCount,
                  });
        },
    },
    methods: {
        getPositionUrl(item) {
            if (this.resolveRoute) return this.resolveRoute(item.jobAdId);
            else return '';
        },
        onViewChange(value) {
            this.viewList = value;
        },
        defaultLogo: () => DefaultLogo,
        hasEmptyListSlot() {
            return this.$slots.empty; // If there is a <slot name='empty' /> provided.
        },
        animate() {
            this.showItems = false;
            this.$nextTick(() => {
                this.showItems = true;
            });
        },
    },
    mounted() {
        if (!this.isLoadingMirror) {
            this.animate();
        }
    },
    watch: {
        data(newData, oldData) {
            let changed = false;
            if (newData.length < oldData.length) {
                this.animate();
                return;
            }
            for (let i = 0; i < oldData.length; i++) {
                if (newData[i].id !== oldData[i].id) {
                    changed = true;
                    break;
                }
            }

            if (changed) {
                this.animate();
            }
        },
        isLoading(newData, oldData) {
            if (!this.showItems && newData != oldData) {
                this.animate();
            }
        },
        fakeLoad(should, was) {
            if (should && !was) {
                this.showItems = false;
            } else if (!should && was) {
                this.showItems = true;
            }
        },
    },
};
</script>

<style scoped>
/* .appear-move {
    transition: opacity .5s linear, transform .5s ease-in-out;
} */

.appear-enter-active {
    transition: opacity 0.2s linear, transform 0.2s cubic-bezier(0.2, 0.5, 0.1, 1);
    transition-delay: calc(0.05s * var(--i));
}

.appear-enter {
    opacity: 0;
    transform: translateY(80px);
}

.grid-view {
    background-color: #f3f3f3;

    display: grid;
    grid-template-columns: 1fr 1fr 1fr;

    grid-gap: 1px;
}

.smallView-grid-view {
    grid-template-columns: 1fr;
}

.overflow-height {
    overflow-y: scroll;
}

@media only screen and (max-width: 800px) {
    .grid-view {
        grid-template-columns: 1fr 1fr;
    }

    .smallView-grid-view {
        grid-template-columns: 1fr;
    }
}

@media only screen and (max-width: 600px) {
    .grid-view {
        grid-template-columns: 1fr;
    }
}

.image {
    max-height: 200px !important;
    max-width: 200px !important;
}
</style>
